import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MarkdownService } from 'ngx-markdown';
import { BehaviorSubject, catchError, of } from 'rxjs';
import { OnlineHelpDocument } from './online-help-document';
import { DialogService } from 'app/core/dialogs/dialog.service';

/**
 * Service responsible for retrieving and displaying online help documents.
 *
 * Loads markdown files from `/assets/help/`, parses them into HTML via ngx-markdown,
 * extracts the title from the first H1 heading, and exposes the active document as
 * a reactive stream. Maintains a navigation history so users can go back and forward
 * through previously viewed help pages.
 */
@Injectable({
  providedIn: 'root',
})
export class OnlineHelpService {
  /** Tracks the ordered list of documents the user has visited */
  private _documentHistory: OnlineHelpDocument[] = [];

  /** Index into `_documentHistory` pointing at the currently displayed document */
  private _currentDocumentIndex = -1;

  /** Emits the currently active help document, or `undefined` when the drawer should close */
  activeDocument$ = new BehaviorSubject<OnlineHelpDocument | undefined>(
    undefined,
  );

  constructor(
    private _http: HttpClient,
    private _markdownService: MarkdownService,
    private _dialogService: DialogService,
  ) {}

  /**
   * Loads a markdown help document from `/assets/help/{filePath}.md`, parses it to HTML,
   * extracts the title from the first H1 heading, and pushes it as the active document
   * which opens the help drawer.
   *
   * @param filePath - Path relative to `/assets/help/` (e.g. `/create-server-config` or `images`)
   */
  showDocument(filePath: string): void {
    let title = 'Unknown Title';

    // Normalize path to always start with /
    if (!filePath.startsWith('/')) {
      filePath = `/${filePath}`;
    }

    // Load the raw markdown file from assets
    this._http
      .get(`/assets/help${filePath}.md`, { responseType: 'text' })
      .pipe(
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe(async (data: string | null) => {
        if (!data) {
          this._dialogService.alert(
            `Could not load help document: ${filePath}`,
          );
          return;
        }

        // Extract the title from the first H1 heading before rendering
        const h1Match = data.match(/^#\s+(.+)$/m);
        if (h1Match) {
          title = h1Match[1].trim();
        }

        // Parse the markdown into HTML (ngx-markdown v21+ returns a Promise)
        const content = await this._markdownService.parse(data, {
          mermaid: true,
        });

        if (content) {
          const doc = new OnlineHelpDocument(title, content as string);
          this.activeDocument$.next(doc);

          // Truncate any forward history when navigating to a new document
          this._documentHistory = this._documentHistory.slice(
            0,
            this._currentDocumentIndex + 1,
          );
          this._documentHistory.push(doc);
          this._currentDocumentIndex = this._documentHistory.length - 1;
        } else {
          this._dialogService.alert(
            `Could not load help document: ${filePath}`,
          );
        }
      });
  }

  /**
   * Hides the currently active document and closes the help drawer.
   */
  hideDocument(): void {
    this.activeDocument$.next(undefined);
  }

  /** Returns true if there is a previous document in the navigation history */
  canGoBack(): boolean {
    return this._currentDocumentIndex > 0;
  }

  /** Returns true if there is a next document in the navigation history */
  canGoForward(): boolean {
    return this._currentDocumentIndex < this._documentHistory.length - 1;
  }

  /** Navigates back to the previous document in the history */
  goBack(): void {
    if (this._currentDocumentIndex > 0) {
      this._currentDocumentIndex--;
      this.activeDocument$.next(
        this._documentHistory[this._currentDocumentIndex],
      );
    }
  }

  /** Navigates forward to the next document in the history */
  goForward(): void {
    if (this._currentDocumentIndex < this._documentHistory.length - 1) {
      this._currentDocumentIndex++;
      this.activeDocument$.next(
        this._documentHistory[this._currentDocumentIndex],
      );
    }
  }
}
